import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { ArrowLeft, Package, Clock, Timer, Sparkles, User as UserIcon, AlertCircle, CheckCircle } from "lucide-react";

const CATEGORY_LABEL = { SOCKS: "Socken", SLIP: "Slips" };

export default function MysteryBagDetailPage() {
  const { t } = useTranslation();
  const { category: rawCat } = useParams();
  const category = (rawCat || "").toUpperCase();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [reservation, setReservation] = useState(null);
  const [remainingSec, setRemainingSec] = useState(0);
  const timerRef = useRef(null);

  const isValidCat = category === "SOCKS" || category === "SLIP";

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    if (!isValidCat) { navigate("/stilltouchedx"); }
  }, [user, navigate, isValidCat]);

  const loadPreview = useCallback(async () => {
    if (!isValidCat) return;
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/rescue/wundertueten/preview/${category}`);
      setPreview(data);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      setError(code || "Fehler beim Laden.");
      setPreview(null);
    } finally { setLoading(false); }
  }, [category, isValidCat]);

  useEffect(() => { loadPreview(); }, [loadPreview]);

  // Restore any active reservation the buyer already holds (F-4-C UX)
  useEffect(() => {
    if (!isValidCat || !user) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/rescue/wundertueten/active/${category}`);
        if (!cancelled && data.active && data.reservation) {
          setReservation(data.reservation);
        }
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [category, isValidCat, user]);

  // Countdown timer for active reservation
  useEffect(() => {
    if (!reservation?.expires_at || reservation.status !== "ACTIVE") {
      if (timerRef.current) clearInterval(timerRef.current);
      setRemainingSec(0);
      return;
    }
    const tick = () => {
      const remaining = Math.max(0, Math.floor((new Date(reservation.expires_at).getTime() - Date.now()) / 1000));
      setRemainingSec(remaining);
      if (remaining === 0) {
        clearInterval(timerRef.current);
        setReservation(null);
        loadPreview();
      }
    };
    tick();
    timerRef.current = setInterval(tick, 1000);
    return () => clearInterval(timerRef.current);
  }, [reservation, loadPreview]);

  const reserve = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.post(`/rescue/wundertueten/reserve/${category}`);
      setReservation(data);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      setError(code === "SOLD_OUT" ? "Aktuell keine Wundertüte verfügbar. Bitte später erneut versuchen." : code || "Reservierung fehlgeschlagen.");
      loadPreview();
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    if (!reservation?.reservation_id) return;
    setBusy(true);
    try {
      await api.post(`/rescue/wundertueten/reservation/${reservation.reservation_id}/cancel`);
      setReservation(null);
      loadPreview();
    } catch (_) { /* ignore */ } finally { setBusy(false); }
  };

  const goCheckout = () => {
    if (!reservation?.reservation_id) return;
    // Payment integration will be added in F-4-D. For now, direct the buyer
    // to the existing checkout entrypoint with the reservation id as context.
    navigate(`/checkout?mystery_reservation=${encodeURIComponent(reservation.reservation_id)}`);
  };

  const label = CATEGORY_LABEL[category] || category;
  const fmtTime = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
  const isAvsVerified = user?.age_verification_status === "VERIFIED";

  return (
    <div data-testid="mystery-detail-page" className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#1A1A1A]">
        <button data-testid="mystery-back" onClick={() => navigate("/stilltouchedx")} className="text-white">
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-base font-bold tracking-tight flex items-center gap-2">
            <Sparkles size={16} className="text-amber-300" /> {label}-Wundertüte
          </h1>
          <p className="text-[10px] text-[#888]">{t("mystery_bag.desc")}</p>
        </div>
      </div>

      <div className="px-4 py-4 space-y-4">
        {loading && <p data-testid="mystery-loading" className="text-xs text-[#666]">{t("mystery_bag.loading_offer")}</p>}

        {!loading && preview && !preview.available && (
          <div data-testid="mystery-soldout" className="p-4 bg-[#1A1A1A] border border-[#333] text-center">
            <Package size={22} className="mx-auto mb-2 text-[#666]" />
            <p className="text-sm font-bold text-white">Aktuell ausverkauft</p>
            <p className="text-[11px] text-[#888] mt-1">
              Sobald wieder Rescue-Artikel aus dem Warehouse-Pool verfügbar sind, kannst du hier bestellen.
              {typeof preview.distinct_sellers === "number" && (
                <> {t("mystery_bag.sellers_in_pool")} <b>{preview.distinct_sellers}</b>.</>
              )}
            </p>
            <button
              data-testid="mystery-refresh"
              onClick={loadPreview}
              className="mt-3 h-9 px-4 bg-white text-[#0A0A0A] text-xs font-semibold rounded"
            >
              Neu prüfen
            </button>
          </div>
        )}

        {!loading && preview?.available && (
          <>
            {/* Price block */}
            <div data-testid="mystery-price-block" className="p-4 bg-[#111] border border-[#222] flex items-baseline justify-between">
              <div>
                <p className="text-[10px] text-[#888] uppercase tracking-wider">{t("mystery_bag.bag_price")}</p>
                <p className="text-3xl font-bold text-white tracking-tight">
                  {Number(preview.mb_price).toFixed(2)} <span className="text-lg text-[#888]">€</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] text-[#888] uppercase tracking-wider">Enthalten</p>
                <p className="text-sm font-medium text-white">{preview.item_count} Artikel</p>
              </div>
            </div>

            {/* Item list */}
            <div>
              <h2 className="text-[10px] uppercase text-[#888] tracking-wider mb-2">{t("mystery_bag.items_you_get")}</h2>
              <div className="grid grid-cols-1 gap-2">
                {preview.items.map((it, i) => (
                  <div
                    key={it.item_id}
                    data-testid={`mystery-item-${i}`}
                    className="flex items-center gap-3 p-2 bg-[#111] border border-[#1A1A1A]"
                  >
                    <div className="w-16 h-16 bg-[#1A1A1A] flex items-center justify-center shrink-0 relative overflow-hidden">
                      {it.thumb_url ? (
                        <img src={it.thumb_url} alt="" className={`w-full h-full object-cover ${isAvsVerified ? "" : "blur-md opacity-60"}`} />
                      ) : (
                        <Package size={22} className="text-[#333]" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-white">
                        {label.replace(/s$/, "")}{it.wear_duration_days ? ` · ${it.wear_duration_days}T getragen` : ""}
                      </p>
                      <p className="text-[10px] text-[#888] flex items-center gap-1 mt-0.5">
                        <UserIcon size={10} />
                        {it.seller_display_name || "anonym"}
                      </p>
                      <p className="text-[10px] text-[#888] mt-0.5">
                        Einzel-Rescue-Preis: {Number(it.rescue_price || 0).toFixed(2)} €
                        {it.status === "EXPIRED" && <span className="ml-1 text-amber-300">(sonst entsorgt)</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Reservation / Checkout */}
            {!reservation && (
              <button
                data-testid="mystery-reserve-btn"
                onClick={reserve}
                disabled={busy}
                className="w-full h-12 bg-white text-[#0A0A0A] text-sm font-semibold rounded disabled:opacity-50"
              >
                {busy ? "Reserviere …" : "Reservieren für 15 Minuten & bezahlen"}
              </button>
            )}

            {reservation && (
              <div data-testid="mystery-reservation-block" className="p-4 bg-emerald-950/40 border border-emerald-700/40 rounded space-y-3">
                <div className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-emerald-400 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-emerald-200">Reservierung aktiv</p>
                    <p className="text-[10px] text-emerald-300/80 mt-0.5">{t("mystery_bag.items_blocked")}</p>
                  </div>
                </div>
                <div data-testid="mystery-countdown" className="flex items-center gap-2 bg-[#0A0A0A]/60 px-3 py-2 rounded">
                  <Timer size={14} className="text-amber-300" />
                  <span className="text-lg font-mono font-bold text-white">{fmtTime(remainingSec)}</span>
                  <span className="text-[10px] text-[#888]">verbleibend</span>
                </div>
                <div className="flex gap-2">
                  <button
                    data-testid="mystery-checkout-btn"
                    onClick={goCheckout}
                    className="flex-1 h-11 bg-white text-[#0A0A0A] text-xs font-semibold rounded"
                  >
                    Jetzt bezahlen · {Number(reservation.mb_price).toFixed(2)} €
                  </button>
                  <button
                    data-testid="mystery-cancel-btn"
                    onClick={cancel}
                    disabled={busy}
                    className="h-11 px-4 bg-[#333] text-white text-xs font-medium rounded"
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {error && (
          <div data-testid="mystery-error" className="p-3 bg-red-900/30 border border-red-800 text-red-300 text-xs flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        <div className="px-1 pt-4 text-[10px] text-[#666] leading-relaxed">
          <p className="mb-1"><Clock size={10} className="inline mr-1" /> {t("mystery_bag.reservation_expires")}</p>
          <p>{t("mystery_bag.price_calculation")}</p>
        </div>
      </div>
    </div>
  );
}
